import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Users, Calendar, Award, Megaphone, Plus, Crown } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import DashboardShell from "@/components/DashboardShell";

const ROLE_META = {
  president: { icon: <Crown className="w-4 h-4" />, label: "President", color: "champagne" },
  vp: { icon: <Award className="w-4 h-4" />, label: "Vice President", color: "rose" },
  secretary: { icon: <Users className="w-4 h-4" />, label: "Secretary", color: "teal" },
};

export default function ChapterDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("about");
  const [ann, setAnn] = useState({ title: "", body: "" });
  const [posting, setPosting] = useState(false);

  const load = () => api.get(`/chapters/${id}`).then(r => setC(r.data)).catch(() => setC(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const role = user?.role === "admin" ? "admin" : "member";

  if (c === null) return (
    <DashboardShell role={role} title="Chapter"><div className="py-24 text-center text-plumSoft">Loading…</div></DashboardShell>
  );
  if (c === false) return (
    <DashboardShell role={role} title="Chapter"><div className="py-24 text-center text-plumSoft">Chapter not found.</div></DashboardShell>
  );

  const isLeader = user && (user.role === "admin" || [c.president_id, c.vp_id, c.secretary_id].includes(user.id));
  const isLive = !((c.founded || "").startsWith("Coming"));

  const postAnnouncement = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      await api.post(`/chapters/${id}/announcements`, ann);
      toast.success("Announcement posted");
      setAnn({ title: "", body: "" });
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not post");
    } finally { setPosting(false); }
  };

  return (
    <DashboardShell role={role} title={c.name} subtitle={c.city}>
      <div data-testid="chapter-detail">
        {/* Hero */}
        <section className="relative h-[320px] overflow-hidden bg-aubergine rounded-2xl mb-8">
          <img src={c.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-t from-aubergine via-aubergine/60 to-aubergine/30" />
          <div className="relative h-full flex flex-col justify-end px-8 pb-8">
            <Link to="/chapters" className="text-xs text-ivory/70 flex items-center gap-2 mb-4"><ArrowLeft className="w-3.5 h-3.5" /> All chapters</Link>
            <div className="inline-flex items-center gap-2 mb-3">
              {isLive ? (
                <span className="inline-flex items-center gap-1.5 bg-rose px-3 py-1 rounded-full text-[10px] tracking-widest uppercase text-ivory font-medium"><span className="w-1.5 h-1.5 rounded-full bg-ivory animate-pulse" /> Live · Founding</span>
              ) : (
                <span className="bg-ivory/15 backdrop-blur px-3 py-1 rounded-full text-[10px] tracking-widest uppercase text-champagne">{c.founded}</span>
              )}
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-ivory leading-none">{c.name}</h1>
            <p className="text-ivory/70 mt-3 flex items-center gap-2"><MapPin className="w-4 h-4" />{c.city}</p>
          </div>
        </section>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { id: "about", label: "About" },
            { id: "leadership", label: "Leadership" },
            { id: "members", label: `Members (${c.members_count})` },
            { id: "events", label: "Events" },
            { id: "announcements", label: "Announcements" },
          ].map(t => (
            <button key={t.id} data-testid={`ch-tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-5 py-2 rounded-full text-xs uppercase tracking-widest border ${tab === t.id ? "bg-aubergine text-ivory border-aubergine" : "border-aubergine/20 text-plum"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "about" && (
          <div className="card-luxe p-8 max-w-3xl">
            <p className="overline">About {c.name}</p>
            <p className="mt-4 text-plumSoft font-light leading-relaxed text-lg">{c.description}</p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              <Box label="Founded" value={c.founded} />
              <Box label="City" value={c.city} />
              <Box label="Members" value={c.members_count} />
            </div>
          </div>
        )}

        {tab === "leadership" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[["president", c.president], ["vp", c.vp], ["secretary", c.secretary]].map(([roleKey, u]) => {
              const meta = ROLE_META[roleKey];
              return (
                <div key={roleKey} className="card-luxe p-7">
                  <div className={`w-12 h-12 rounded-full bg-${meta.color}/15 text-${meta.color} flex items-center justify-center`}>{meta.icon}</div>
                  <p className="overline mt-5">{meta.label}</p>
                  {u ? (
                    <>
                      <Link to={`/members/${u.id}`} className="font-display text-2xl text-aubergine mt-2 block hover:text-rose transition-colors">{u.name}</Link>
                      <p className="text-sm text-plumSoft mt-1 font-light">{u.business || u.industry || "—"}</p>
                    </>
                  ) : (
                    <p className="font-display text-xl text-plumSoft/60 mt-2">Position open</p>
                  )}
                </div>
              );
            })}
            <div className="md:col-span-3 mt-4 card-luxe p-7 bg-lavender/40">
              <p className="overline mb-4">Role responsibilities</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-plumSoft font-light">
                <Resp title="President" items={["Lead chapter activities", "Approve announcements", "Oversee events", "View analytics & reports"]} />
                <Resp title="Vice President" items={["Coordinate members", "Organize chapter activities", "Support President", "Drive member engagement"]} />
                <Resp title="Secretary" items={["Manage attendance", "Maintain meeting records", "Post announcements", "Handle member communications"]} />
              </div>
            </div>
          </div>
        )}

        {tab === "members" && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {c.members_list?.length > 0 ? c.members_list.map(m => (
              <Link key={m.id} to={`/members/${m.id}`} className="card-luxe overflow-hidden group">
                <div className="aspect-square overflow-hidden bg-lavender">
                  {m.avatar
                    ? <img src={m.avatar} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    : <div className="w-full h-full flex items-center justify-center text-plum font-display text-5xl">{m.name?.[0]}</div>
                  }
                </div>
                <div className="p-4">
                  <p className="font-display text-base text-aubergine">{m.name}</p>
                  <p className="text-xs text-plumSoft mt-0.5">{m.business || m.industry || "—"}</p>
                </div>
              </Link>
            )) : <p className="md:col-span-4 text-plumSoft text-center py-10 font-light">No members in this chapter yet.</p>}
          </div>
        )}

        {tab === "events" && (
          <div className="space-y-3">
            {c.upcoming_events?.length > 0 ? c.upcoming_events.map(e => (
              <div key={e.id} className="card-luxe p-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-lg text-aubergine">{e.title}</p>
                  <p className="text-xs text-plum mt-1 flex items-center gap-3">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {new Date(e.date).toLocaleDateString("en-IN", { dateStyle: "long" })}</span>
                    <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {e.location}</span>
                  </p>
                </div>
                <Link to="/events" className="text-sm text-plum link-underline">View →</Link>
              </div>
            )) : <p className="text-plumSoft text-center py-10 font-light">No upcoming chapter events.</p>}
          </div>
        )}

        {tab === "announcements" && (
          <div className="space-y-4 max-w-3xl">
            {isLeader && (
              <form onSubmit={postAnnouncement} className="card-luxe p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Megaphone className="w-4 h-4 text-rose" />
                  <p className="overline">New announcement (leadership only)</p>
                </div>
                <input data-testid="ann-title" required placeholder="Title…" value={ann.title} onChange={e => setAnn({ ...ann, title: e.target.value })} className="input-luxe mb-3" />
                <textarea data-testid="ann-body" required rows={4} placeholder="Body…" value={ann.body} onChange={e => setAnn({ ...ann, body: e.target.value })} className="input-luxe" />
                <button data-testid="ann-post" disabled={posting} className="btn-primary mt-4 inline-flex items-center gap-2"><Plus className="w-3.5 h-3.5" /> {posting ? "Posting…" : "Post"}</button>
              </form>
            )}
            {c.announcements?.length > 0 ? c.announcements.map(a => (
              <div key={a.id} className="card-luxe p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-display text-xl text-aubergine">{a.title}</p>
                  <p className="text-xs text-plumSoft">{new Date(a.created_at).toLocaleDateString("en-IN")}</p>
                </div>
                <p className="text-sm text-plumSoft font-light leading-relaxed whitespace-pre-line">{a.body}</p>
                <p className="text-xs text-plum mt-3">— {a.author_name}</p>
              </div>
            )) : <p className="text-plumSoft text-center py-10 font-light">No announcements yet.</p>}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

function Box({ label, value }) {
  return (
    <div className="text-center">
      <p className="font-display text-3xl text-aubergine">{value || "—"}</p>
      <p className="text-[10px] tracking-widest uppercase text-plum mt-1">{label}</p>
    </div>
  );
}

function Resp({ title, items }) {
  return (
    <div>
      <p className="font-display text-aubergine">{title}</p>
      <ul className="mt-2 space-y-1">
        {items.map(i => <li key={i} className="flex items-start gap-2"><span className="w-1 h-1 rounded-full bg-rose mt-2 flex-none" />{i}</li>)}
      </ul>
    </div>
  );
}
